import { useSyncExternalStore } from "react";
import { NetworkError, api } from "../api";
import type { SpellRequest, SpellResponse } from "./protocol";

/**
 * Spellcheck state shared by every editor: the dictionary worker, a cache of answers, and
 * the user's personal dictionary. Lookups are synchronous; a word not yet in the cache
 * counts as correct until the worker answers, and listeners are told when that changes
 * what should be underlined.
 */

/** Straight apostrophes, so "don’t" and "don't" are one word. */
export const normalizeWord = (w: string) => w.replace(/’/g, "'");

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

/** Called when underlines may need redrawing (dictionary answers or personal words changed). */
export function onSpellingChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---- dictionary worker ---------------------------------------------------------

const answers = new Map<string, boolean>();
const asked = new Set<string>();
let queue: string[] = [];
let worker: Worker | null = null;
let nextId = 1;
const suggestionWaiters = new Map<number, (words: string[]) => void>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = (e: MessageEvent<SpellResponse>) => {
      const msg = e.data;
      if (msg.type === "checked") {
        let wrong = false;
        for (const [w, ok] of msg.results) {
          answers.set(w, ok);
          if (!ok) wrong = true;
        }
        // Unanswered words were drawn as correct, so only misspellings change anything.
        if (wrong) notify();
      } else {
        suggestionWaiters.get(msg.id)?.(msg.words);
        suggestionWaiters.delete(msg.id);
      }
    };
  }
  return worker;
}

const send = (msg: SpellRequest) => getWorker().postMessage(msg);

function ask(word: string) {
  if (asked.has(word)) return;
  asked.add(word);
  if (!queue.length) {
    setTimeout(() => {
      send({ type: "check", words: queue });
      queue = [];
    });
  }
  queue.push(word);
}

/** False when the word is known to be wrong; unknown words are looked up in the background. */
export function isCorrect(word: string): boolean {
  const w = normalizeWord(word);
  if (personalKeys.has(w.toLowerCase())) return true;
  const known = answers.get(w);
  if (known === undefined) {
    ask(w);
    return true;
  }
  return known;
}

export function suggest(word: string): Promise<string[]> {
  const id = nextId++;
  return new Promise((resolve) => {
    suggestionWaiters.set(id, resolve);
    send({ type: "suggest", id, word: normalizeWord(word) });
  });
}

// ---- personal dictionary ---------------------------------------------------------

type PendingOp = { op: "add" | "remove"; word: string };

let userId: string | null = null;
/** As the user typed them, alphabetical. */
let personal: string[] = [];
let personalKeys = new Set<string>();

const wordsKey = () => `quill.dictionary.${userId}`;
const pendingKey = () => `quill.dictionary.pending.${userId}`;

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the server copy is still updated.
  }
}

const collator = new Intl.Collator(undefined, { sensitivity: "base" });

function setPersonal(words: string[]) {
  personal = [...words].sort(collator.compare);
  personalKeys = new Set(personal.map((w) => normalizeWord(w).toLowerCase()));
  save(wordsKey(), personal);
  notify();
}

let flushing: Promise<boolean> = Promise.resolve(true);

/**
 * Sends queued changes in order (made offline, or not yet acknowledged); stops at the first
 * network failure and keeps the rest. One flush at a time; new changes are only appended.
 */
function flushPending(): Promise<boolean> {
  flushing = flushing.then(async () => {
    const key = pendingKey();
    for (let ops = load<PendingOp[]>(key, []); ops.length; ops = load<PendingOp[]>(key, [])) {
      const { op, word } = ops[0]!;
      try {
        await (op === "add" ? api.addDictionaryWord(word) : api.removeDictionaryWord(word));
      } catch (err) {
        if (err instanceof NetworkError) return false;
        // Rejected by the server (e.g. not a word): drop it rather than retry forever.
      }
      save(key, load<PendingOp[]>(key, []).slice(1));
    }
    return true;
  });
  return flushing;
}

async function refreshFromServer() {
  if (!(await flushPending())) return;
  try {
    const { words } = await api.dictionary();
    // A change made while this was in flight is newer than the server's answer.
    if (!load<PendingOp[]>(pendingKey(), []).length) setPersonal(words);
  } catch {
    // Offline: keep the cached list.
  }
}

/** Loads the signed-in user's dictionary (cached copy first) and keeps it in sync. */
export function openDictionary(id: string): () => void {
  userId = id;
  setPersonal(load<string[]>(wordsKey(), []));
  void refreshFromServer();
  const online = () => void refreshFromServer();
  window.addEventListener("online", online);
  return () => {
    window.removeEventListener("online", online);
    userId = null;
    personal = [];
    personalKeys = new Set();
  };
}

function change(op: PendingOp["op"], word: string) {
  if (!userId) return;
  const key = normalizeWord(word).toLowerCase();
  const rest = personal.filter((w) => normalizeWord(w).toLowerCase() !== key);
  setPersonal(op === "add" ? [...rest, word] : rest);
  save(pendingKey(), [...load<PendingOp[]>(pendingKey(), []), { op, word }]);
  void flushPending();
}

export const addToDictionary = (word: string) => change("add", word);
export const removeFromDictionary = (word: string) => change("remove", word);

export function usePersonalDictionary(): string[] {
  return useSyncExternalStore(onSpellingChange, () => personal);
}
